import { useState, useCallback } from 'react'
import {
  headlessGoogleDriveAuthUrl,
  headlessGoogleDriveStatus,
  headlessGoogleDriveFolders,
  headlessGoogleDriveFiles,
  headlessGoogleDriveDisconnect,
} from 'src/api/headless'

const useGoogleDrive = () => {
  const [connected, setConnected] = useState(false)
  const [needsReconsent, setNeedsReconsent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [folders, setFolders] = useState(null)
  const [files, setFiles] = useState([])
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [breadcrumb, setBreadcrumb] = useState([])
  const [error, setError] = useState(null)

  const reset = useCallback(() => {
    setFolders(null)
    setFiles([])
    setSelectedFolder(null)
    setBreadcrumb([])
    setError(null)
  }, [])

  const checkStatus = useCallback(async () => {
    try {
      setLoading(true)
      const data = await headlessGoogleDriveStatus()
      setConnected(data.connected)
      setNeedsReconsent(data.needs_reconsent || false)
    } catch (e) {
      setError(e.message || 'Failed to check status')
    } finally {
      setLoading(false)
    }
  }, [])

  const connect = useCallback(async () => {
    try {
      setLoading(true)
      const data = await headlessGoogleDriveAuthUrl()
      const popup = window.open(
        data.authUrl,
        'google-drive-auth',
        'width=600,height=700',
      )

      let timer

      const handleMessage = event => {
        if (event.data?.type === 'google-drive-oauth') {
          const { status, message } = event.data
          if (status === 'connected') {
            checkStatus()
          } else if (status === 'error') {
            setError(message || 'Failed to connect Google Drive')
          }

          window.removeEventListener('message', handleMessage)
          clearInterval(timer)
          if (popup && !popup.closed) {
            popup.close()
          }
        }
      }

      window.addEventListener('message', handleMessage)

      timer = setInterval(() => {
        if (popup && popup.closed) {
          window.removeEventListener('message', handleMessage)
          clearInterval(timer)
          checkStatus()
        }
      }, 1000)
    } catch (e) {
      setError(e.message || 'Failed to get auth URL')
    } finally {
      setLoading(false)
    }
  }, [checkStatus])

  const browseFolders = useCallback(
    async (parentId = 'root') => {
      try {
        setLoading(true)
        setError(null)
        if (parentId === 'root') {
          setBreadcrumb([])
        }
        const data = await headlessGoogleDriveFolders(parentId)
        setFolders(data.folders || [])
      } catch (e) {
        setError(e.message || 'Failed to list folders')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const openFolder = useCallback(
    (folderId, folderName) => {
      setBreadcrumb(prev => [...prev, { id: folderId, name: folderName }])
      browseFolders(folderId)
    },
    [browseFolders],
  )

  const navigateTo = useCallback(
    index => {
      const crumb = breadcrumb[index]
      setBreadcrumb(prev => prev.slice(0, index + 1))
      browseFolders(crumb.id)
    },
    [breadcrumb, browseFolders],
  )

  const selectFolder = useCallback(
    async (folderId, folderName) => {
      try {
        setLoadingFiles(true)
        setFiles([])
        setSelectedFolder({ id: folderId, name: folderName })
        const data = await headlessGoogleDriveFiles(folderId)
        setFiles(data.files || [])
      } catch (e) {
        setError(e.message || 'Failed to list files')
      } finally {
        setLoadingFiles(false)
      }
    },
    [],
  )

  const disconnect = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await headlessGoogleDriveDisconnect()
      setConnected(false)
      setNeedsReconsent(false)
      setFolders([])
      setFiles([])
      setSelectedFolder(null)
      setBreadcrumb([])
    } catch (e) {
      setError(e.message || 'Failed to disconnect')
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    connected,
    needsReconsent,
    loading,
    loadingFiles,
    folders,
    files,
    selectedFolder,
    breadcrumb,
    error,
    checkStatus,
    connect,
    disconnect,
    browseFolders,
    openFolder,
    navigateTo,
    selectFolder,
    reset,
  }
}

export default useGoogleDrive
